import { useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  COMPANY_ROOT,
  COMPANY_ROOT_ID,
  ancestorIdsOf,
  deaccent,
} from './orgChartHelpers';

/**
 * Turns the flat node list from `GET /organization/employees/org_chart` into the
 * tree the canvas draws, plus the derived sets the toolbar needs.
 *
 * Two defensive rules matter here, both caused by the same backend bug:
 * `Organization::Employee#change_manager!` rewrites one row's ancestry with
 * `update_all` and never rewrites its descendants'. So the data can contain
 *
 *   - a `parent_id` pointing at somebody who is not in the payload, and
 *   - an outright cycle (A reports to B, B reports to A).
 *
 * A tree builder that assumes neither would drop people silently or recurse
 * forever. Both cases are handled and counted, so the page can say so out loud.
 */

const SEARCH_FIELDS = ['searchName', 'searchFullName', 'searchTitle', 'searchEmail', 'searchHrCode'];

/**
 * Fuzzy search is the FALLBACK, not the primary match — and it is deliberately
 * strict.
 *
 * Vietnamese names are built from a small set of short syllables, so a loose
 * fuzzy pass is close to useless here: at threshold 0.34, "loi" matched "Lê
 * Hoàng Tùng", "Lưu Bảo Trâm" and 66 others, lighting up the whole chart. A
 * tight threshold plus a minimum match length keeps this pass for what it is
 * actually good at — typos — instead of letting it approximate everything.
 */
const FUSE_OPTIONS = {
  includeScore: false,
  threshold: 0.2,
  minMatchCharLength: 3,
  ignoreLocation: true,
  keys: [
    { name: 'searchName', weight: 3 },
    { name: 'searchFullName', weight: 3 },
    { name: 'searchTitle', weight: 1 },
    { name: 'searchEmail', weight: 1 },
    { name: 'searchHrCode', weight: 2 },
  ],
};

/**
 * Plain substring match on the de-accented fields. This is what people actually
 * expect when they type part of a name, and it is exact: "loi" finds "Nguyễn Tấn
 * Lợi" and nobody else.
 */
export const substringMatches = (employees, needle) =>
  employees.filter((employee) => SEARCH_FIELDS.some((field) => (employee[field] || '').includes(needle)));

/**
 * Substring first; fuzzy only when nothing matched literally, so a typo still
 * finds someone but a correctly-typed query is never diluted by approximations.
 */
export const searchEmployees = (employees, fuse, rawQuery) => {
  const term = deaccent((rawQuery || '').trim());
  if (term.length < 2) return null; // null = "no search running", ≠ empty set

  const exact = substringMatches(employees, term);
  if (exact.length > 0) return new Set(exact.map((employee) => employee.id));

  return new Set(fuse.search(term).map((hit) => hit.item.id));
};

/** Attach the de-accented fields Fuse searches on. */
const withSearchFields = (employee) => ({
  ...employee,
  searchName: deaccent(employee.name),
  searchFullName: deaccent(employee.full_name),
  searchTitle: deaccent(employee.title),
  searchEmail: deaccent(employee.email),
  searchHrCode: deaccent(employee.hr_code),
});

/**
 * Build parent→children adjacency, resolving the two dirty-data cases above.
 * Returns childrenByParent plus the anomalies found, so nothing is hidden.
 */
// Exported for tests: the dirty-data handling below is the part most worth
// pinning down, and it is far easier to assert on directly than through a hook.
export const buildAdjacency = (employees) => {
  const byId = new Map(employees.map((e) => [e.id, e]));
  const childrenByParent = new Map();
  const parentById = new Map();
  const orphanedParentIds = [];
  const brokenCycleIds = [];

  const addChild = (parentId, childId) => {
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(childId);
    parentById.set(childId, parentId);
  };

  employees.forEach((employee) => {
    const parentId = employee.parent_id;

    if (parentId === null || parentId === undefined) {
      addChild(COMPANY_ROOT_ID, employee.id);
      return;
    }

    // Manager not in the payload (deactivated, or ancestry left dangling by
    // change_manager!). Treat as a root rather than losing the person.
    if (!byId.has(parentId)) {
      orphanedParentIds.push(employee.id);
      addChild(COMPANY_ROOT_ID, employee.id);
      return;
    }

    if (parentId === employee.id) {
      brokenCycleIds.push(employee.id);
      addChild(COMPANY_ROOT_ID, employee.id);
      return;
    }

    addChild(parentId, employee.id);
  });

  // Cycle detection: walk each node's manager chain; if we come back to a node
  // already on the current path, cut that edge and reattach at the root.
  const state = new Map(); // id -> 'visiting' | 'safe'
  employees.forEach((employee) => {
    if (state.get(employee.id) === 'safe') return;

    const path = [];
    let cursor = employee.id;

    while (cursor !== undefined && cursor !== COMPANY_ROOT_ID) {
      if (state.get(cursor) === 'safe') break;

      if (state.get(cursor) === 'visiting') {
        // `cursor` closes a loop — detach it from its manager.
        const badParent = parentById.get(cursor);
        if (badParent !== undefined) {
          const siblings = childrenByParent.get(badParent) || [];
          childrenByParent.set(badParent, siblings.filter((id) => id !== cursor));
          addChild(COMPANY_ROOT_ID, cursor);
          brokenCycleIds.push(cursor);
        }
        break;
      }

      state.set(cursor, 'visiting');
      path.push(cursor);
      cursor = parentById.get(cursor);
    }

    path.forEach((id) => state.set(id, 'safe'));
  });

  return { byId, childrenByParent, parentById, orphanedParentIds, brokenCycleIds };
};

/**
 * Everyone with no manager AND no reports. Hanging hundreds of these off the
 * company root would produce a wall of one-node columns and bury the real tree,
 * so they go to a tray instead — which doubles as HR's to-do list.
 *
 * Deactivated people are kept OUT of that tray: the list exists to say "these
 * need a manager", and somebody who has left does not. They are counted rather
 * than dropped, so the tray can admit that it is not showing everything.
 */
export const splitUnassigned = (childrenByParent, byId) => {
  const rootChildren = childrenByParent.get(COMPANY_ROOT_ID) || [];
  const attached = [];
  const unassigned = [];
  let inactiveUnassigned = 0;

  rootChildren.forEach((id) => {
    const hasReports = (childrenByParent.get(id) || []).length > 0;
    if (hasReports) {
      attached.push(id);
      return;
    }

    const employee = byId.get(id);
    if (employee?.active === false) inactiveUnassigned += 1;
    else unassigned.push(employee);
  });

  return { attached, unassigned, inactiveUnassigned };
};

const useOrgChartData = ({ employees, filters, query, collapsedIds }) => {
  const indexed = useMemo(() => (employees || []).map(withSearchFields), [employees]);

  const graph = useMemo(() => buildAdjacency(indexed), [indexed]);

  const fuse = useMemo(() => new Fuse(indexed, FUSE_OPTIONS), [indexed]);

  const matchedIds = useMemo(
    () => searchEmployees(indexed, fuse, query),
    [indexed, fuse, query],
  );

  // Filters are an AND across the three dropdowns; an empty dropdown means "any".
  const filteredIds = useMemo(() => {
    const { siteIds = [], departmentIds = [], status = '' } = filters || {};
    const noFilter = siteIds.length === 0 && departmentIds.length === 0 && status === '';
    if (noFilter) return null;

    const matches = indexed.filter((employee) => {
      if (siteIds.length && !siteIds.includes(employee.site_id)) return false;
      if (departmentIds.length && !departmentIds.includes(employee.department_id)) return false;
      if (status === 'sales' && !employee.is_sales) return false;
      if (status === 'bod' && !employee.is_bod) return false;
      return true;
    });

    return new Set(matches.map((e) => e.id));
  }, [indexed, filters]);

  /**
   * A node stays on the canvas if it matches, or if it is an ancestor of
   * something that matches. Dropping non-matching managers instead would snap
   * the reporting chain and leave their reports floating with no context.
   */
  const visibility = useMemo(() => {
    const active = filteredIds || matchedIds;
    if (!active) return { visibleIds: null, contextIds: new Set() };

    const visibleIds = new Set([COMPANY_ROOT_ID]);
    const contextIds = new Set();

    active.forEach((id) => {
      visibleIds.add(id);
      ancestorIdsOf(id, graph.parentById).forEach((ancestorId) => {
        if (!active.has(ancestorId)) contextIds.add(ancestorId);
        visibleIds.add(ancestorId);
      });
    });

    return { visibleIds, contextIds };
  }, [filteredIds, matchedIds, graph.parentById]);

  /** Ancestors of every search hit, so the chart can open the path to them. */
  const pathToMatches = useMemo(() => {
    if (!matchedIds || matchedIds.size === 0) return new Set();

    const path = new Set();
    matchedIds.forEach((id) => {
      ancestorIdsOf(id, graph.parentById).forEach((ancestorId) => path.add(ancestorId));
    });
    path.add(COMPANY_ROOT_ID);
    return path;
  }, [matchedIds, graph.parentById]);

  const { attached, unassigned, inactiveUnassigned } = useMemo(
    () => splitUnassigned(graph.childrenByParent, graph.byId),
    [graph],
  );

  /**
   * The tree actually rendered: company root, then only visible descendants,
   * stopping at collapsed nodes. Children keep the payload order (name asc from
   * the API) so the layout is stable between renders.
   */
  const tree = useMemo(() => {
    const { visibleIds, contextIds } = visibility;
    const isVisible = (id) => !visibleIds || visibleIds.has(id);

    const buildNode = (employee, depth) => {
      const childIds = (graph.childrenByParent.get(employee.id) || []).filter(isVisible);
      const collapsed = collapsedIds.has(employee.id) && childIds.length > 0;

      return {
        ...employee,
        depth,
        collapsed,
        childCount: childIds.length,
        totalCount: (graph.childrenByParent.get(employee.id) || []).length,
        isMatch: matchedIds ? matchedIds.has(employee.id) : false,
        isContext: contextIds.has(employee.id),
        children: collapsed ? [] : childIds.map((id) => buildNode(graph.byId.get(id), depth + 1)),
      };
    };

    const rootChildIds = attached.filter(isVisible);
    const rootCollapsed = collapsedIds.has(COMPANY_ROOT_ID) && rootChildIds.length > 0;

    return {
      ...COMPANY_ROOT,
      depth: 0,
      collapsed: rootCollapsed,
      childCount: rootChildIds.length,
      totalCount: attached.length,
      isMatch: false,
      isContext: false,
      children: rootCollapsed ? [] : rootChildIds.map((id) => buildNode(graph.byId.get(id), 1)),
    };
  }, [graph, attached, visibility, collapsedIds, matchedIds]);

  const visibleUnassigned = useMemo(() => {
    const { visibleIds } = visibility;
    if (!visibleIds) return unassigned;
    return unassigned.filter((employee) => visibleIds.has(employee.id));
  }, [unassigned, visibility]);

  return {
    tree,
    unassigned: visibleUnassigned,
    inactiveUnassigned,
    matchedIds,
    matchedList: useMemo(
      () => (matchedIds ? indexed.filter((e) => matchedIds.has(e.id)) : []),
      [indexed, matchedIds],
    ),
    pathToMatches,
    childrenByParent: graph.childrenByParent,
    parentById: graph.parentById,
    byId: graph.byId,
    anomalies: {
      orphanedParents: graph.orphanedParentIds.length,
      brokenCycles: graph.brokenCycleIds.length,
    },
  };
};

export default useOrgChartData;
